import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import {
  AppSettingsService,
  FeatureToggleService,
  GroupContextService,
  GroupSettingsService,
  hd,
  ITicketConfiguration,
  TicketContextService,
  TicketService,
} from '@nx-fe/services';
import IActivityModel = hd.IActivityModel;
import { XsI18nPipe } from '@nx-fe/pipes';
import { ActivityContextService } from 'xsmodules/ticket/components/ticket-details-panel/activity/activity-context';
import { Activity, ActivityTypeEnum, isNoteActivity, NoteActivity } from 'xsmodules/ticket/ticket.model';
import { NoteComponent } from 'xsmodules/ticket/components/ticket-details-panel/activity/note-activity/note/note.component';
import { TicketPageService } from 'xsmodules/ticket/ticket-page.service';
import { take } from 'rxjs/operators';

@Component({
  selector: 'xs-note-activity',
  templateUrl: './note-activity.component.html',
  styleUrls: ['./note-activity.component.scss'],
})
export class NoteActivityComponent implements OnInit {
  @Input() activity: Activity;
  @Input() activityCtrl: ActivityContextService;
  @Output() updatePrivacyIcon = new EventEmitter<string>();
  @Output() isEditting = new EventEmitter<boolean>();
  @Output() refresh = new EventEmitter<void>();

  isNotePrivate: boolean;
  isResolutionNote: boolean;

  private _ticketConfiguration: ITicketConfiguration;
  private note: NoteActivity;
  private noteState: string;
  public error: string;
  private editing = false;
  public disableNote = false;

  private _canEdit: boolean;

  constructor(
    private featureToggleService: FeatureToggleService,
    private ticketContextService: TicketContextService,
    private ticketService: TicketService,
    private xsI18nPipe: XsI18nPipe,
    private groupSettingsService: GroupSettingsService,
    private ticketPageService: TicketPageService
  ) {}

  @ViewChild(NoteComponent) noteComponent: NoteComponent;

  ngOnInit(): void {
    this.note = this.activity as NoteActivity;
    if (isNoteActivity(this.activity)) {
      this.isNotePrivate = this.activity.details.isPrivate;
      this.isResolutionNote = this.activity.details.isResolution;
    }

    if (this.isResolutionNote) {
      this.ticketPageService
        .getTicketConfig()
        .pipe(take(1))
        .subscribe((ticketConfig: ITicketConfiguration) => {
          this._ticketConfiguration = ticketConfig;
        });
    }
    this.setupCustomIconCallback();
  }

  get canEdit(): boolean {
    if (this.featureToggleService.isEnabled('AbacAllowableActionsGroups')) {
      return this.ticketContextService.canEditNote();
    }
    return this._canEdit;
  }

  /** Determines whether this is a new note or note */
  get isNew(): boolean {
    if (this.isResolutionNote) {
      return this.activity.creationDate === this.activity.updateDate || !this.activity.updateDate;
    }
    return !this.activity.activityId;
  }

  public getHeader = (): string => {
    if (this.isResolutionNote) {
      return this.xsI18nPipe.transform('mandatoryNote_Note_Caption');
    }

    if (this.isNew) {
      if (this.isNotePrivate) {
        return this.xsI18nPipe.transform('note_private_NewNote_Caption');
      } else {
        return this.xsI18nPipe.transform('note_NewNote_Caption');
      }
    } else {
      if (this.isNotePrivate) {
        return this.xsI18nPipe.transform('note_private_activity_Caption');
      } else {
        return this.xsI18nPipe.transform('note_public_activity_Caption');
      }
    }
  };

  get isNoteRequiredOnResolve(): boolean {
      return !!(this._ticketConfiguration?.isNoteRequiredOnResolve && this.ticketPageService.status === 'resolved');
  }
    
  get showResolutionNoteWarning(): boolean {
    return this._ticketConfiguration?.isNoteRequiredOnResolve;
  }

  get editText(): string {
    if (this.isResolutionNote) {
      return this.xsI18nPipe.transform('note_UpdatedBy_Caption', this.activityCtrl.updateUser);
    } else {
      return this.xsI18nPipe.transform('note_AddedBy_Caption', this.activityCtrl.creationUser);
    }
  }

  public updatePrivacy(event) {
    this.isNotePrivate = event;

    if (this.isResolutionNote) {
      this.updatePrivacyIcon.emit('hd-icon hd-icon-resolution-note');
    } else if (this.isNotePrivate) {
      this.updatePrivacyIcon.emit('hd-icon hd-icon-noteprivate');
    } else {
      this.updatePrivacyIcon.emit('hd-icon hd-icon-note');
    }
  }

  public saveNote(event: NoteActivity) {
    let isAllowed = this.isNotePrivate
      ? this.ticketContextService.canAddPrivateNote()
      : this.ticketContextService.canAddPublicNote();
    if (!isAllowed) {
      this.error = this.xsI18nPipe.transform('error_Http_Forbidden');
      return;
    }
    if (event.description.trim().length === 0 && !this.canSaveEmptyNote()) {
      if (this.note.details.isResolution) {
        this.error = this.xsI18nPipe.transform('note_Error', this.xsI18nPipe.transform('note_Resolution_Required'));
      } else {
        this.error = this.xsI18nPipe.transform('note_Error', this.xsI18nPipe.transform('note_Save_Empty'));
      }
    } else {
      this.error = '';
      this.disableNote = true;
      const temp = {
        activityId: this.note.activityId,
        activityType: ActivityTypeEnum[this.note.activityTypeId],
        noteText: event.description,
        isPrivate: event.details.isPrivate,
        isResolution: this.isResolutionNote,
      };

      // TODO: Remove forced types once types are tidied up
      this.ticketService
        .saveActivity((<unknown>temp) as IActivityModel, this.ticketContextService.ticket.ticketId)
        .subscribe(
          data => {
            const saveResult = <any>data;

            // It's crucial to maintain the reference, as this activity is propogated throughout the code
            Object.assign(this.activity, this.note, {
              activityId: saveResult.activityId,
              description: event.description,
              details: {
                isPrivate: event.details.isPrivate,
                isResolution: event.details.isResolution,
              },
            });

            this.ticketService
              .getActivity(parseInt(this.ticketContextService.ticket.ticketId, 10), this.activity.activityId)
              .subscribe(result => {
                const activity = (<unknown>result) as Activity;
                if (typeof activity.details === 'object' && Object.keys(activity.details).includes('0')) {
                  activity.details = activity.details[0];
                }

                // crucial to maintain reference, replacing it would break the reference chain
                Object.assign(this.activity, activity);

                // Trigger activity setter to force interal user updates (this is the *same* activity)
                this.activityCtrl.Activity = this.activity;

                this.disableNote = false;
                this.updateActivityState();
              });
            this.refresh.emit();
          },
          error => {
            this.error = this.xsI18nPipe.transform('note_save_not_allowed');
            console.error(error);
            this.disableNote = false;
            this.updateActivityState();
          }
        );
    }
  }

  public updateEditing(event) {
    this.editing = event;
    this.isEditting.emit(event);
    this.updateActivityState();
  }

  /** Updates the activity state based on various attributes of the activity, model, and template */
  public updateActivityState(): void {
    if (this.error) {
      this.noteState = 'error';
    } else if ((this.isNew && !this.note.details.isResolution) || this.editing) {
      this.noteState = 'active';
    } else {
      this.noteState = 'inactive';
    }
    this.activityCtrl.setActivityState(this.noteState);
  }

  private setupCustomIconCallback() {
    this.note.getCustomIconClass = () => {
      if (this.isResolutionNote) {
        return 'hd-icon hd-icon-resolution-note';
      } else {
        return this.isNotePrivate ? 'hd-icon hd-icon-noteprivate' : 'hd-icon hd-icon-note';
      }
    };
  }

  private canSaveEmptyNote(): boolean {
    return this.isResolutionNote && !this.isNoteRequiredOnResolve;
  }
}
